import { DEFAULT_LOCALE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { BypassableValues, isTruthy } from "@vega-core/utils/isTruthy";

export const formatNumber = (
  value?: number | null,
  rounding: Rounding = Rounding.NoFractions,
) =>
  value !== null && value !== undefined
    ? Intl.NumberFormat(DEFAULT_LOCALE, {
        compactDisplay: "short",
        ...getRoundingAttributes(rounding),
      }).format(value)
    : "-";

export function formatPercentage(
  percentage: number,
  rounding: Rounding = Rounding.Max2Fractions,
) {
  return `${formatNumber(percentage * 100, rounding)}%`;
}
/**
 * Formats a basis point (1/100th of a percent) value.
 * Rounds the value according to the specified rounding strategy.
 * Appends a 'bps' suffix to the result.
 * @param basisPoint The basis point value to format, must not be null or undefined.
 * @param rounding The rounding strategy to apply, defaults to maximum 2 fractions.
 * @returns A string representing the formatted basis point value.
 */
export function formatBasePoint(
  basisPoint: number,
  rounding: Rounding = Rounding.Max2Fractions,
) {
  return `${formatNumber(basisPoint * 10_000, rounding)} bps`;
}
/**
 * Formats a MOIC (multiple on invested capital) value.
 * Rounds the value according to the specified rounding strategy.
 * Appends an 'x' suffix to the result.
 * @param moic The MOIC value to format, must not be null or undefined.
 * @param rounding The rounding strategy to apply, defaults to always 1 fraction.
 * @returns A string representing the formatted MOIC value.
 */
export function formatMoic(
  moic: number,
  rounding: Rounding = Rounding.Always1Fractions,
) {
  return `${formatNumber(moic, rounding)}x`;
}

/**
 * Calculates the change between current and previous values.
 * Returns the raw number without formatting.
 * @param current The current value
 * @param previous The previous value
 * @returns A number representing the change (current - previous)
 */
export function calculateChange(current: number, previous: number) {
  return current - previous;
}

/**
 * Formats a percentage range from a given minimum and maximum percentage.
 * If only one value is provided, formats it as a single percentage.
 * If no values are provided, returns an empty string.
 * @param minPercentage the minimum percentage, optional
 * @param maxPercentage the maximum percentage, optional
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X-Y%" where X and Y are the formatted percentages, or just "X%" if only one value is provided
 */
export function formatPercentageRange(
  minPercentage?: number | null,
  maxPercentage?: number | null,
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  if (
    isTruthy(minPercentage, BypassableValues.Zero) &&
    isTruthy(maxPercentage, BypassableValues.Zero)
  ) {
    const formattedMin = formatNumber(minPercentage * 100, rounding);
    const formattedMax = formatPercentage(maxPercentage, rounding);

    return `${formattedMin}-${formattedMax}`;
  } else if (isTruthy(minPercentage, BypassableValues.Zero)) {
    return formatPercentage(minPercentage, rounding);
  } else if (isTruthy(maxPercentage, BypassableValues.Zero)) {
    return formatPercentage(maxPercentage, rounding);
  } else {
    return "-";
  }
}

/**
 * Formats a percentage range from an array of percentage values.
 * Creates a range from the array, sorts min and max values, then formats them.
 * If only one value is provided, formats it as a single percentage.
 * If no values are provided, returns an empty string.
 * @param percentageArray the array of percentage values to format
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X-Y%" where X and Y are the formatted percentages, or just "X%" if only one value is provided
 */
export function formatPercentageRangeArray(
  percentageArray: (number | null | undefined)[],
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  const validPercentages = percentageArray
    .filter((p) => isTruthy(p, BypassableValues.Zero))
    .sort((a, b) => a! - b!);

  if (validPercentages.length === 0) {
    return "-";
  } else if (validPercentages.length === 1) {
    return formatPercentage(validPercentages[0]!, rounding);
  } else {
    const formattedMin = formatPercentage(validPercentages[0]!, rounding);
    const formattedMax = formatPercentage(
      validPercentages[validPercentages.length - 1]!,
      rounding,
    );

    return `${formattedMin}-${formattedMax}`;
  }
}

/**
 * Formats a number range from a given minimum and maximum number.
 * If only one value is provided, formats it as a single number.
 * If no values are provided, returns an empty string.
 * @param minNumber the minimum number, optional
 * @param maxNumber the maximum number, optional
 * @param rounding the rounding to apply when formatting, defaults to 2 decimal places
 * @returns a string in the format "X-Y" where X and Y are the formatted numbers, or just "X" if only one value is provided
 */
export function formatNumberRange(
  minNumber?: number | null,
  maxNumber?: number | null,
  rounding: Rounding = Rounding.Max2Fractions,
): string {
  if (
    isTruthy(minNumber, BypassableValues.Zero) &&
    isTruthy(maxNumber, BypassableValues.Zero)
  ) {
    const formattedMin = formatNumber(minNumber, rounding);
    const formattedMax = formatNumber(maxNumber, rounding);

    return `${formattedMin}-${formattedMax}`;
  } else if (isTruthy(minNumber, BypassableValues.Zero)) {
    return formatNumber(minNumber, rounding);
  } else if (isTruthy(maxNumber, BypassableValues.Zero)) {
    return formatNumber(maxNumber, rounding);
  } else {
    return "-";
  }
}

export const formatFileSize = (size: number | null | undefined) => {
  if (isFalsy(size)) {
    return "-";
  }

  const formatLocal = (localSize: number) =>
    Intl.NumberFormat(DEFAULT_LOCALE, {
      compactDisplay: "short",
      notation: "compact",
    }).format(localSize);

  const kilobyte = 1024;
  const megabyte = kilobyte * 1024;
  const gigabyte = megabyte * 1024;

  if (size < kilobyte) {
    return m.fileSize_bytes({ size: formatLocal(size) });
  }

  if (size < megabyte) {
    return m.fileSize_kilobytes({ size: formatLocal(size / kilobyte) });
  }

  if (size < gigabyte) {
    return m.fileSize_megabytes({ size: formatLocal(size / megabyte) });
  }

  return m.fileSize_gigabytes({ size: formatLocal(size / gigabyte) });
};

export enum Rounding {
  Always2Fractions,
  Always1Fractions,
  Max2Fractions,
  Max3Fractions,
  NoFractions,
}

export function getRoundingAttributes(roundingStrategy: Rounding) {
  switch (roundingStrategy) {
    case Rounding.NoFractions:
      return {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      };
    case Rounding.Max2Fractions:
      return {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      };
    case Rounding.Max3Fractions:
      return {
        maximumFractionDigits: 3,
        minimumFractionDigits: 0,
      };
    case Rounding.Always1Fractions:
      return {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      };
    case Rounding.Always2Fractions:
    default:
      return {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      };
  }
}
